// @ts-nocheck
import { useEffect, useState, useCallback } from 'react';
import {
  deleteAmenity,
  getAmenities,
  updateAmenity,
  reorderAmenities
} from '../services/amenity/amenity';
import { ErrorToast, SuccessToast } from '../utils/alerts';
import { useTranslation } from 'react-i18next';

const useGlobalAmenities = () => {
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddAmenityFormVisible, setIsAddAmenityFormVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteAmenityId, setDeleteAmenityId] = useState(null);

  const { t } = useTranslation(['errors', 'trip']);

  const handleDeleteAmenityConfirmation = async () => {
    try {
      await deleteAmenity(deleteAmenityId);
      setAmenitiesList((prev) =>
        prev.filter((amenity) => amenity.amenityid !== deleteAmenityId)
      );
    } catch (error) {
      ErrorToast(
        t('errors:amenityCouldNotBeDeleted', { reason: error.message })
      );
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteAmenityId(null);
    }
  };

  const handleUpdateAmenity = async (amenity) => {
    try {
      setIsSubmitting(true);

      await updateAmenity(amenity.amenityid, amenity);

      // Update the amenities list with the updated amenity
      setAmenitiesList((prevList) =>
        prevList.map((item) =>
          item.amenityid === amenity.amenityid ? amenity : item
        )
      );
    } catch (error) {
      ErrorToast(
        t('errors:amenityCouldNotBeUpdated', { reason: error.message })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAmenities = useCallback(async () => {
    const amenities = await getAmenities();
    const list = (amenities?.data ?? []).sort((a, b) => a?.seq - b?.seq);
    setAmenitiesList(list);
  }, []);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const revertAmenityOrder = (
    sourceAmenityId = 0,
    destinationAmenityId = 0
  ) => {
    const newAmenities = [...amenitiesList];

    const sourceAmenityIndex = amenitiesList.findIndex(
      (amenity) => amenity.amenityid === sourceAmenityId
    );

    const destinationAmenityIndex = amenitiesList.findIndex(
      (amenity) => amenity.amenityid === destinationAmenityId
    );

    const tempAmenity = newAmenities[sourceAmenityIndex];
    newAmenities[sourceAmenityIndex] = newAmenities[destinationAmenityIndex];
    newAmenities[destinationAmenityIndex] = tempAmenity;

    setAmenitiesList(newAmenities);
  };

  const handleUpdateSequence = async (sourceAmenity, destinationAmenity) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const sourceIndex = amenitiesList.findIndex(
      (amenity) => amenity.amenityid === sourceAmenity.amenityid
    );

    const destIndex = amenitiesList.findIndex(
      (amenity) => amenity.amenityid === destinationAmenity.amenityid
    );

    if (sourceIndex === -1 || destIndex === -1) {
      setIsSubmitting(false);
      return;
    }

    // Create a copy of the current list to manipulate
    const newList = [...amenitiesList];

    // Remove the item from its original position
    const [movedItem] = newList.splice(sourceIndex, 1);

    // Insert it at the new position
    newList.splice(destIndex, 0, movedItem);

    // Update sequences based on new positions
    const updatedList = newList.map((item, index) => ({
      ...item,
      seq: index + 1
    }));

    // Optimistically update the UI
    setAmenitiesList(updatedList);

    // Send update to server - update only affected sequences with a single request
    try {
      // Determine the range of elements that need sequence updates
      const startIndex = Math.min(sourceIndex, destIndex);
      const endIndex = Math.max(sourceIndex, destIndex);

      // Create array with only the amenities that need sequence updates
      const amenitiesForUpdate = [];
      for (let i = startIndex; i <= endIndex; i++) {
        const amenity = updatedList[i];
        amenitiesForUpdate.push({
          amenityid: amenity.amenityid,
          sequence: i + 1
        });
      }

      // Update sequences on the server with a single request
      await reorderAmenities(amenitiesForUpdate);

      // Success - affected sequences updated correctly
      SuccessToast(t('trip:savedAmenities'));
    } catch (error) {
      console.error('Error updating sequence:', error);
      ErrorToast(t('errors:amenitySequenceCouldNotBeUpdated'));
      // Revert to original order if there was an error
      revertAmenityOrder(sourceAmenity.amenityid, destinationAmenity.amenityid);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAmenity = async (amenityId) => {
    setIsDeleteModalOpen(true);
    setDeleteAmenityId(amenityId);
  };

  return {
    amenitiesList,
    isSubmitting,
    isAddAmenityFormVisible,
    isDeleteModalOpen,
    handleDeleteAmenityConfirmation,
    handleUpdateAmenity,
    handleDeleteAmenity,
    handleUpdateSequence,
    setIsAddAmenityFormVisible,
    setAmenitiesList,
    setIsDeleteModalOpen,
    setDeleteAmenityId
  };
};

export default useGlobalAmenities;
